<script setup>
import { Head, useForm, usePage } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import { computed, ref } from 'vue';
import { trackEvent } from '@/lib/tracking';
import { useDesignSet } from '@/composables/useDesignSet';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    spotImages: Object,
});

const { isClassic } = useDesignSet();
const page = usePage();

const accent = props.domain?.settings?.brand_accent
    || props.domain?.settings?.brand_color
    || '#ea580c';

const domainName = computed(() => props.domain?.request_host || props.domain?.name || 'leadportal.scienceworks.com');
const contactEmail = computed(() => props.domain?.settings?.contact_email || (domainName.value.includes('scienceworks.com') ? 'info@scienceworks.com' : `info@${domainName.value}`));
const serviceLabel = computed(() => props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik');
const isBrandDirect = computed(() => checkBrandDirect(props.domain));
const brandName = computed(() => props.domain?.settings?.brand_name || domainName.value);

const form = useForm({
    name: '',
    email: '',
    subject: '',
    message: '',
    service_type: props.service || 'pv',
    website: '', // honeypot
});

const submitted = computed(() => page.props.flash?.contact_status === 'success');
const submitting = ref(false);

const submit = () => {
    if (!form.name || !form.email || !form.message) return;

    trackEvent('contact_form_submit', {
        event_category: 'engagement',
        service_type: props.service,
        metadata: { subject: form.subject },
    });

    form.post('/kontakt', {
        preserveScroll: true,
        onStart: () => { submitting.value = true; },
        onFinish: () => { submitting.value = false; },
        onSuccess: () => { form.reset('name', 'email', 'subject', 'message'); },
    });
};
</script>

<template>
    <Head :title="`Kontakt - ${serviceLabel}-Beratung`">
        <meta name="description" :content="`Kontaktieren Sie uns für eine kostenlose Beratung zu ${serviceLabel}. Unser Team berät Sie gerne.`" />
        <meta property="og:title" :content="`Kontakt - ${serviceLabel}-Beratung`" />
        <meta property="og:type" content="website" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="contact" heroMode :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
            <div class="pvhx-contact" :style="{ '--pvhx-accent': accent }">
                <!-- Hero -->
                <section class="pvhx-hero">
                    <div class="pvhx-wrap">
                        <p class="pvhx-eyebrow">Kontakt</p>
                        <h1 class="pvhx-h1">Frage zu PV plus Wärme?</h1>
                        <p class="pvhx-lead">Schreiben Sie uns. Wir vermitteln an regionale Fachbetriebe für Photovoltaik und Wärmepumpe.</p>
                    </div>
                </section>

                <!-- Contact body -->
                <section class="pvhx-section">
                    <div class="pvhx-wrap">
                        <div class="pvhx-contact-grid">
                            <!-- Left: details -->
                            <div class="pvhx-details">
                                <div class="pvhx-trust">
                                    <span class="pvhx-trust__item">Kostenlos &amp; unverbindlich</span>
                                    <span class="pvhx-trust__sep" aria-hidden="true">/</span>
                                    <span class="pvhx-trust__item">Antwort in der Regel</span>
                                    <span class="pvhx-trust__sep" aria-hidden="true">/</span>
                                    <span class="pvhx-trust__item">Kostenlos</span>
                                </div>

                                <div class="pvhx-detail">
                                    <h3 class="pvhx-detail__label">E-Mail</h3>
                                    <a :href="`mailto:${contactEmail}`" class="pvhx-detail__link">{{ contactEmail }}</a>
                                    <p class="pvhx-detail__hint">Wir antworten in der Regel innerhalb von 24 Stunden.</p>
                                </div>

                                <div class="pvhx-detail">
                                    <h3 class="pvhx-detail__label">Erreichbarkeit</h3>
                                    <p class="pvhx-detail__text">Mo - Fr: 9:00 - 17:00 Uhr</p>
                                    <p class="pvhx-detail__hint">Anfragen über das Formular werden auch außerhalb der Geschäftszeiten bearbeitet.</p>
                                </div>

                                <div class="pvhx-detail pvhx-detail--accent">
                                    <h3 class="pvhx-detail__label">Über uns</h3>
                                    <p v-if="isBrandDirect" class="pvhx-detail__text">{{ brandName }} ist Ihr Fachbetrieb für {{ serviceLabel }} - Beratung, Planung und Installation aus einer Hand. Persönlich, zuverlässig, regional.</p>
                                    <p v-else class="pvhx-detail__text">Wir sind ein Vergleichsportal und verbinden Hauseigentümer mit qualifizierten Fachbetrieben für {{ serviceLabel }} in ganz Deutschland. Unser Service ist für Sie kostenlos und unverbindlich.</p>
                                </div>

                                <div
                                    v-if="spotImages?.contact_illustration"
                                    class="pvhx-contact-img"
                                    role="img"
                                    aria-label="Kontakt"
                                    :style="{ backgroundImage: `url(${spotImages.contact_illustration})` }"
                                ></div>
                            </div>

                            <!-- Right: form -->
                            <div class="pvhx-form-card">
                                <div v-if="submitted" class="pvhx-form-success">
                                    <div class="pvhx-form-success__icon" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" fill="none" width="34" height="34"><path d="M20 6 9 17l-5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" /></svg>
                                    </div>
                                    <h2 class="pvhx-form-success__title">Vielen Dank, Ihre Nachricht ist angekommen.</h2>
                                    <p class="pvhx-form-success__text">Wir melden uns in der Regel innerhalb von 24 Stunden bei Ihnen. Bei dringenden Anliegen erreichen Sie uns auch direkt unter <a :href="`mailto:${contactEmail}`" class="pvhx-form-success__link">{{ contactEmail }}</a>.</p>
                                </div>
                                <form v-else @submit.prevent="submit" class="pvhx-form" novalidate>
                                    <h2 class="pvhx-form__title">Nachricht senden</h2>
                                    <p class="pvhx-form__intro">Alle mit * markierten Felder sind Pflichtfelder. Wir melden uns in der Regel innerhalb von 24 Stunden.</p>
                                    <div class="pvhx-form__row">
                                        <div class="pvhx-form__field">
                                            <label for="contact-name" class="pvhx-form__label">Ihr Name *</label>
                                            <input
                                                id="contact-name"
                                                v-model="form.name"
                                                type="text"
                                                placeholder="Max Mustermann"
                                                class="pvhx-form__input"
                                                :class="{ 'pvhx-form__input--error': form.name === '' && form.isDirty }"
                                                required
                                                autocomplete="name"
                                            />
                                            <span v-if="form.errors?.name" class="pvhx-form__error" role="alert">{{ form.errors.name }}</span>
                                        </div>
                                        <div class="pvhx-form__field">
                                            <label for="contact-email" class="pvhx-form__label">Ihre E-Mail *</label>
                                            <input
                                                id="contact-email"
                                                v-model="form.email"
                                                type="email"
                                                placeholder="max@beispiel.de"
                                                class="pvhx-form__input"
                                                :class="{ 'pvhx-form__input--error': form.errors?.email }"
                                                required
                                                autocomplete="email"
                                            />
                                            <span v-if="form.errors?.email" class="pvhx-form__error" role="alert">{{ form.errors.email }}</span>
                                        </div>
                                    </div>
                                    <div class="pvhx-form__field">
                                        <label for="contact-subject" class="pvhx-form__label">Betreff</label>
                                        <input
                                            id="contact-subject"
                                            v-model="form.subject"
                                            type="text"
                                            placeholder="Worum geht es?"
                                            class="pvhx-form__input"
                                        />
                                    </div>
                                    <div class="pvhx-form__field">
                                        <label for="contact-message" class="pvhx-form__label">Ihre Nachricht *</label>
                                        <textarea
                                            id="contact-message"
                                            v-model="form.message"
                                            rows="5"
                                            placeholder="Beschreiben Sie Ihr Anliegen..."
                                            class="pvhx-form__textarea"
                                            :class="{ 'pvhx-form__input--error': form.errors?.message }"
                                            required
                                        ></textarea>
                                        <span v-if="form.errors?.message" class="pvhx-form__error" role="alert">{{ form.errors.message }}</span>
                                    </div>
                                    <!-- Honeypot: hidden field bots fill in -->
                                    <div aria-hidden="true" style="position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;">
                                        <label>Website (bitte leer lassen)</label>
                                        <input type="text" v-model="form.website" tabindex="-1" autocomplete="off" />
                                    </div>
                                    <button type="submit" :disabled="submitting" class="pvhx-form__submit">
                                        <svg v-if="submitting" class="pvhx-form__spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" stroke-dasharray="31.4 31.4" stroke-linecap="round"><animateTransform attributeName="transform" type="rotate" dur="0.8s" from="0 12 12" to="360 12 12" repeatCount="indefinite"/></circle></svg>
                                        {{ submitting ? 'Wird gesendet...' : 'Nachricht absenden' }}
                                    </button>
                                    <p class="pvhx-form__privacy">
                                        Projektanfragen können an Fachbetriebe weitergegeben werden. Details in der Datenschutzerklärung.
                                    </p>
                                </form>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </VariantPageWrapper>
    </AppLayout>
</template>

<style scoped>
/* pvhx-contact-* / shared pvhx-* namespace, matched to PvKostenrechnerChrome /
   Home.vue (paper #f4f1ea, ink #1c1917, amber accent, Lora + Inter). */
.pvhx-contact {
    --pvhx-ink: #1c1917;
    --pvhx-ink-2: #44403c;
    --pvhx-mute: #78716c;
    --pvhx-page: #f4f1ea;
    --pvhx-surface: #ffffff;
    --pvhx-line: #e4d8c6;
    --pvhx-line-2: #cfc0a8;
    background: var(--pvhx-page);
    color: var(--pvhx-ink);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 16px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
}

.pvhx-contact h1,
.pvhx-contact h2,
.pvhx-contact h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    letter-spacing: -0.015em;
}

.pvhx-wrap {
    max-width: 1240px;
    margin: 0 auto;
    padding: 0 32px;
}

@media (max-width: 600px) {
    .pvhx-wrap { padding: 0 20px; }
}

.pvhx-eyebrow {
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--pvhx-accent, #ea580c);
    font-weight: 600;
    margin: 0 0 18px;
}

.pvhx-h1 {
    font-size: clamp(38px, 5.4vw, 60px);
    line-height: 1.02;
    letter-spacing: -0.025em;
    color: var(--pvhx-ink);
    margin: 0 0 20px;
    max-width: 18ch;
}

.pvhx-lead {
    font-size: 18px;
    line-height: 1.6;
    color: var(--pvhx-ink-2);
    max-width: 58ch;
    margin: 0;
}

/* Hero */
.pvhx-hero {
    padding: 88px 0 56px;
    border-bottom: 1px solid var(--pvhx-line);
    position: relative;
}
.pvhx-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 90% -20%, rgba(234, 88, 12, 0.12), transparent 45%);
}
.pvhx-hero .pvhx-eyebrow,
.pvhx-hero .pvhx-h1,
.pvhx-hero .pvhx-lead { position: relative; }

/* Section */
.pvhx-section {
    padding: 64px 0 88px;
}

.pvhx-contact-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
}

@media (min-width: 1024px) {
    .pvhx-contact-grid { grid-template-columns: 1fr 1fr; gap: 48px; }
}

/* Details */
.pvhx-details {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.pvhx-trust {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 14px 18px;
    background: rgba(234, 88, 12, 0.12);
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
}

.pvhx-trust__item {
    font-size: 13px;
    font-weight: 600;
    color: var(--pvhx-ink);
}

.pvhx-trust__sep {
    color: var(--pvhx-line-2);
    font-size: 12px;
    user-select: none;
}

@media (max-width: 600px) {
    .pvhx-trust__sep { display: none; }
    .pvhx-trust { justify-content: center; }
}

.pvhx-detail {
    padding: 22px 24px;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
}

.pvhx-detail--accent {
    background: rgba(234, 88, 12, 0.12);
    border-left: 3px solid var(--pvhx-accent, #ea580c);
}

.pvhx-detail__label {
    font-size: 15px;
    color: var(--pvhx-ink);
    margin: 0 0 6px;
}

.pvhx-detail__link {
    font-size: 15px;
    font-weight: 600;
    color: var(--pvhx-accent, #ea580c);
    text-decoration: none;
}
.pvhx-detail__link:hover { text-decoration: underline; text-underline-offset: 3px; }

.pvhx-detail__text {
    font-size: 14px;
    line-height: 1.6;
    color: var(--pvhx-ink-2);
    margin: 0;
}

.pvhx-detail__hint {
    font-size: 13px;
    color: var(--pvhx-mute);
    line-height: 1.5;
    margin: 6px 0 0;
}

.pvhx-contact-img {
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 6px;
    border: 1px solid var(--pvhx-line);
    background-size: cover;
    background-position: center;
}

/* Form */
.pvhx-form-card {
    padding: 36px;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
    align-self: start;
}

@media (max-width: 600px) {
    .pvhx-form-card { padding: 24px; }
}

.pvhx-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.pvhx-form__title {
    font-size: 22px;
    color: var(--pvhx-ink);
    margin: 0;
    padding-bottom: 14px;
    border-bottom: 1px solid var(--pvhx-line);
}

.pvhx-form__intro {
    font-size: 14px;
    color: var(--pvhx-mute);
    line-height: 1.5;
    margin: -6px 0 0;
}

.pvhx-form__row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
}

@media (min-width: 640px) {
    .pvhx-form__row { grid-template-columns: 1fr 1fr; }
}

.pvhx-form__field {
    display: flex;
    flex-direction: column;
}

.pvhx-form__label {
    font-size: 13px;
    font-weight: 600;
    color: var(--pvhx-ink);
    margin-bottom: 6px;
}

.pvhx-form__input,
.pvhx-form__textarea {
    width: 100%;
    padding: 12px 16px;
    background: var(--pvhx-page);
    border: 1px solid var(--pvhx-line-2);
    border-radius: 4px;
    font-family: inherit;
    font-size: 15px;
    color: var(--pvhx-ink);
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.pvhx-form__input { min-height: 48px; }
.pvhx-form__textarea { min-height: 120px; resize: vertical; }

.pvhx-form__input:focus,
.pvhx-form__textarea:focus {
    border-color: var(--pvhx-accent, #ea580c);
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.12);
}

.pvhx-form__input::placeholder,
.pvhx-form__textarea::placeholder {
    color: var(--pvhx-mute);
}

.pvhx-form__input--error {
    border-color: #c0392b !important;
    box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12) !important;
}

.pvhx-form__error {
    display: block;
    margin-top: 5px;
    font-size: 13px;
    color: #c0392b;
    line-height: 1.4;
}

.pvhx-form__submit {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 52px;
    padding: 14px 24px;
    background: var(--pvhx-accent, #ea580c);
    color: #fff;
    border: none;
    border-radius: 4px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.15s ease, background 0.15s ease, opacity 0.15s ease;
}

.pvhx-form__submit:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #ea580c;
}

.pvhx-form__submit:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.pvhx-form__spinner {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
}

.pvhx-form__privacy {
    font-size: 12px;
    color: var(--pvhx-mute);
    line-height: 1.5;
    margin: -4px 0 0;
}

/* Success */
.pvhx-form-success {
    text-align: center;
    padding: 30px 0;
}

.pvhx-form-success__icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(234, 88, 12, 0.12);
    color: var(--pvhx-accent, #ea580c);
}

.pvhx-form-success__title {
    font-size: 22px;
    color: var(--pvhx-ink);
    margin: 0 0 10px;
}

.pvhx-form-success__text {
    font-size: 15px;
    line-height: 1.6;
    color: var(--pvhx-ink-2);
    margin: 0;
}

.pvhx-form-success__link {
    color: var(--pvhx-accent, #ea580c);
    font-weight: 600;
}

@media (max-width: 600px) {
    .pvhx-hero { padding: 56px 0 40px; }
    .pvhx-section { padding: 48px 0 72px; }
}

@media (prefers-reduced-motion: reduce) {
    .pvhx-contact *, .pvhx-contact *::before, .pvhx-contact *::after {
        transition: none !important;
        animation: none !important;
    }
}
</style>